/**
 * FeedBlocks — колонка блоков-лент раздела (секции `trend_feed`).
 *
 * Вынесено из BotsTrends (12.09) вместе с useFeedBlocks: владелец попросил такие же блоки на
 * странице изображений. Компонент знает только свой раздел (`page`) и общий пул карточек —
 * клик и ссылка ведут на КАНОНИЧЕСКИЙ лендинг карточки (/bots/<page>/tool/<slug>), как было
 * у трендов; карточке без слага остаётся прямое применение в виджет.
 *
 * ФОРМА КАРТОЧЕК — ПО РАЗДЕЛУ (владелец 12.09: «в разделе Изображения → инструменты пропорции
 * карточек мы не меняем, это полностью другие карточки»). Тренды рисуют свои плитки 9:16/16:10
 * (TrendFeedSection), остальные разделы — ТУ ЖЕ карточку, что в сетке страницы
 * (PopularModelCard из buildPopularCardView), поэтому формат плиток и «Главный тренд» там
 * ничего не решают и в админке приглушены. От ленты остаётся шапка, подложка и состав;
 * раскладка выбирает только сетку или полку с прокруткой — обе не трогают форму карточки.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'

import SliderRow from '../../../components/SliderRow.tsx'
import { useNavigate } from 'react-router-dom'

import TrendFeedSection, { type TrendFeedCardData } from '../../../components/TrendFeedSection.tsx'
import { contrastVars, normHex } from '../../../utils/feedTheme'
import feedStyles from '../../../components/TrendFeedSection.module.scss'
import { PopularModelCard } from '../../../components/PopularModelCard.tsx'
import { buildPopularCardView } from '../utils/popularCardView.ts'
import { gatePromoByCost } from '../../../utils/discount'
import { cardInputVideoContext, computeModelCost } from '../../../utils/modelCost'
import { useProviders } from '../../../hooks/useProviders.ts'
import { useSelectedModel } from '../../../context/SelectedModelContext.tsx'
import { useBotsWidget } from '../context/BotsWidgetContext.tsx'
import { applyPopularItem, findTrendMemberHome, makeFindModel, rawCardToItem, type BackendCard } from '../utils/popularCardApply.ts'
import { cardCtxUrl, type ToolPage } from '../utils/cardSeo.ts'
import { usesPageCards, type FeedBlock } from '../hooks/useFeedBlocks.ts'

interface Props {
	page: ToolPage
	blocks: FeedBlock[]
	/** Все карточки секций страницы — по id находим исходную карточку плитки. */
	rawPool: BackendCard[]
	/** Стафф-превью (?preview=1): скрытые блоки показываются с пометкой. */
	isPreview?: boolean
	/** Контекст витрины для ссылок плиток (зона трендов, категория): клик оставляет человека там,
	 *  откуда он кликнул. Пусто — корневой лендинг, как на страницах без контекста. */
	landingCtx?: string | null
	/** show_stats секции «Популярное» страницы: блок обязан показывать цену так же, как сетка. */
	showStats?: boolean
}

/**
 * Сетка блока с лимитом по РЯДАМ и кнопкой «Показать ещё».
 *
 * Зачем. Блок из 19 карточек разворачивался на пять рядов и съедал полтора
 * экрана, а таких блоков на странице семь. Полка эту задачу решает прокруткой,
 * сетка — раскрытием (владелец 14.09 смотрел оба и попросил третий вариант).
 *
 * Лимит считаем в РЯДАХ, а не в карточках: число колонок плавает от ширины
 * окна, и фиксированное «10 штук» обрывало бы ряд посередине на одних экранах
 * и ровно на других. Админка так и спрашивает — «Сколько показывать сразу:
 * один ряд / два ряда / все» (visible_rows). Блоки, настроенные до этого,
 * несут число плиток (visible_count) — из него ряды вычисляются делением.
 *
 * Хвост монтируется ТОЛЬКО при раскрытии: карточки несут превью и ролики, и
 * тянуть их ради свёрнутого блока — пустой расход трафика.
 */
function BlockGrid({ cards, visibleCount, visibleRows }: { cards: ReactNode[]; visibleCount: number; visibleRows: number }) {
	const ref = useRef<HTMLDivElement>(null)
	const [cols, setCols] = useState(0)
	const [open, setOpen] = useState(false)

	useLayoutEffect(() => {
		const el = ref.current
		if (!el) return
		const read = () => setCols(getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length)
		read()
		const ro = new ResizeObserver(read)
		ro.observe(el)
		return () => ro.disconnect()
	}, [])

	// До первого замера показываем ровно столько, сколько просила админка: иначе
	// первый кадр выкладывал бы ВЕСЬ состав и блок схлопывался бы на глазах.
	// Ряды из админки (visibleRows) — точная величина; visibleCount остаётся для
	// блоков, настроенных до появления рядов.
	const limit =
		cols > 0
			? (visibleRows > 0 ? visibleRows : Math.max(1, Math.floor(visibleCount / cols))) * cols
			: visibleRows > 0
				? visibleRows * 4
				: visibleCount
	const shown = open ? cards : cards.slice(0, limit)
	const hidden = cards.length - shown.length

	return (
		<>
			<div className="bots-popular-grid" ref={ref}>{shown}</div>
			{hidden > 0 && (
				<button type="button" className="bots-feed-block__more" onClick={() => setOpen(true)}>
					Показать ещё {hidden}
				</button>
			)}
		</>
	)
}

export default function FeedBlocks({ page, blocks, rawPool, isPreview = false, showStats = true, landingCtx = null }: Props) {
	const navigate = useNavigate()
	const { brands } = useProviders()
	const { selectModel } = useSelectedModel()
	const { handleStyleApply } = useBotsWidget()
	const findModel = useMemo(() => makeFindModel(brands), [brands])

	// Гейт промо: плашка «−N%» не обещает скидку, которой не будет (параметры карточки
	// дороже её цены — бэк спишет расчёт). Зеркало плиток «Популярных» и бэка.
	const gateCardPromo = useCallback(
		<T extends BackendCard>(c: T): T => {
			const m = c.target_adapter && c.target_model ? findModel(c.target_adapter, c.target_model) : null
			return gatePromoByCost(c, computeModelCost(m, c.settings, cardInputVideoContext(m, c)))
		},
		[findModel],
	)

	const openCard = useCallback(
		(raw: BackendCard) => {
			const url = cardCtxUrl(raw, page, landingCtx)
			if (url) {
				// skipAnchor: кликнувший УЖЕ смотрит на плитку — авто-скролл якоря нужен
				// только пришедшим по ссылке извне.
				navigate(url, { state: { skipAnchor: true } })
				return
			}
			applyPopularItem(rawCardToItem(raw), undefined, {
				category: page,
				findModel,
				handleStyleApply,
				selectModel,
				navigate,
			})
		},
		[page, landingCtx, navigate, findModel, handleStyleApply, selectModel],
	)

	// Тренды рисуют блок своими плитками; остальные разделы — карточками страницы.
	const pageCards = usesPageCards(page)

	const renderPageCardBlock = (b: FeedBlock) => {
		const bgHex = normHex(b.bgColor)
		const themeStyle = bgHex ? ({ background: bgHex, ...contrastVars(bgHex) } as CSSProperties) : undefined
		// Полка с прокруткой — раскладки «лента»/«слайдер»; остальное ложится сеткой
		// раздела. Размер карточки в обоих случаях один и тот же: полка берёт ширину
		// из переменной, которую ставит сама витрина по своей колонке (см.
		// PopularModelsTab) — иначе карточка в полке оставалась бы в своём фиксе и
		// блок выглядел мельче сетки под ним (замечание владельца 14.09).
		const shelf = b.arrangement === 'row' || b.arrangement === 'slider'
		// Раскладка «Главный тренд» держит карточку в hero, а не в members: без
		// этого блок с ней вышел бы на страницу пустой шапкой (плиток нет —
		// рисовать нечего). Здесь hero — обычная карточка раздела.
		const list = b.members.length ? b.members : b.hero ? [b.hero] : []
		if (list.length === 0) return null
		const cards = list.map((card, i) => {
			const { item, cost, rating, users } = buildPopularCardView(card, i, {
				findModel,
				category: page,
				landingCtx,
			})
			return (
				<PopularModelCard
					key={`${item.id}-${i}`}
					item={item}
					realCost={showStats ? cost : undefined}
					realRating={showStats ? rating : undefined}
					realUsers={showStats ? users : undefined}
					onClick={() => openCard(card)}
					href={item.primaryUrl ?? undefined}
					showCta
					compact
				/>
			)
		})
		return (
			<section
				className={`bots-feed-block${bgHex ? ' bots-feed-block--bg' : ''}${
					b.cardSize === 'sm' ? ' bots-feed-block--sm' : ''
				}`}
				style={themeStyle}
			>
				<div className={feedStyles.head}>
					{/* Пустая иконка = иконки нет: огонёк подставлялся кодом, и убрать его
					    из админки было невозможно (владелец 14.09). */}
					{b.icon && <div className={feedStyles.icon}>{b.icon}</div>}
					<div>
						<h2 className={feedStyles.title}>
							{b.title}
							{/* Счётчик только у полки: там часть карточек уехала за край, и
							    сколько их всего — видно только цифрой. У сетки это и так
							    говорит «Показать ещё N» (просьба владельца 14.09). */}
							{shelf && list.length > 1 && <span className="bots-feed-block__count">{list.length}</span>}
						</h2>
						{b.subtitle && <div className={feedStyles.subtitle}>{b.subtitle}</div>}
					</div>
				</div>
				{shelf ? (
					<SliderRow rowClassName="bots-feed-block__row" label={b.title}>
						{cards}
					</SliderRow>
				) : (
					<BlockGrid cards={cards} visibleCount={b.visibleCount} visibleRows={b.visibleRows} />
				)}
			</section>
		)
	}

	if (blocks.length === 0) return null

	return (
		<div className={`bots-feed-blocks${pageCards ? ' bots-feed-blocks--page' : ''}`}>
			{blocks.map((b) => (
				<div key={b.section.id} className={isPreview && b.hidden ? 'bots-feed-blocks__hidden' : undefined}>
					{isPreview && b.hidden && (
						<div className="bots-feed-blocks__hidden-tag">скрыт — видите только вы (превью)</div>
					)}
					{pageCards ? (
						renderPageCardBlock(b)
					) : (
						<TrendFeedSection
							title={b.title}
							subtitle={b.subtitle}
							icon={b.icon}
							layout={b.arrangement}
							tile={b.tile}
							cards={b.members.map(gateCardPromo)}
							visibleCount={b.visibleCount}
							visibleRows={b.visibleRows}
							cardSize={b.cardSize}
							theme={b.theme}
							bgColor={b.bgColor}
							heroCard={b.hero ? gateCardPromo(b.hero) : b.hero}
							hoverAction={(b.section.config as { hover_action?: unknown }).hover_action === true}
							onPick={(c: TrendFeedCardData) => {
								const raw = rawPool.find((x) => x.id === c.id)
								if (raw) { openCard(raw); return }
								// Герой ленты — участник мульти-тренда (из пула спрятан): лендинг его группы с ним.
								const home = c.slug ? findTrendMemberHome(rawPool, c.slug) : null
								const url = home ? cardCtxUrl(home.group, page, landingCtx) : null
								if (home && url) navigate(url, { state: { trendMemberId: home.memberId, skipAnchor: true } })
							}}
							onPickMember={(c: TrendFeedCardData, memberId: string) => {
								// Кадр версии на плитке группы (23.10): её лендинг сразу с этой версией.
								const raw = rawPool.find((x) => x.id === c.id)
								const url = raw ? cardCtxUrl(raw, page, landingCtx) : null
								if (url) navigate(url, { state: { trendMemberId: memberId, skipAnchor: true } })
							}}
							cardHref={(c: TrendFeedCardData) => {
								// Плитки блоков — ссылки на лендинги: с корня раздела канонические (SEO-перелинковка), а с витрины
								// со своим контекстом — чтобы клик по карточке не уводил в соседнюю зону трендов.
								const raw = rawPool.find((x) => x.id === c.id)
								const card = raw ?? (c.slug ? findTrendMemberHome(rawPool, c.slug)?.group : undefined)
								return card ? (cardCtxUrl(card, page, landingCtx) ?? undefined) : undefined
							}}
						/>
					)}
				</div>
			))}
		</div>
	)
}
